<template>
  <div class="document_html">
    <loader-dots v-if="isLoading.textHtml || isLoading.textBlock || isLoading.search" #text>
      {{ `${isLoading.search? 'Поиск совпадений ...': 'Загрузка страницы ...'}` }}
    </loader-dots>

    <iframe
      ref="frame"
      class="document__iframe"
      src="about:blank"
      sandbox="allow-same-origin allow-scripts"
    ></iframe>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import apiDocuments from '@src/api/documents';

import LoaderDots from '@@/shared/loaders/LoaderDots';

import NormaCsLinkHandler from '@@/document/mixins/NormaCsLinkHandler';

export default {
  components: {
    LoaderDots
  },

  mixins: [NormaCsLinkHandler],

  props: {
    card: {
      type: Object,
      default: null
    },
    documentId: {
      type: String,
      required: true
    },
    fragmentId: {
      type: String,
      default: () => null
    },
    viewMode: {
      type: String,
      default: () => 'html'
    },
    scrollTop: {
      type: Number,
      default: () => 0
    }
  },

  data: () => ({
    frameWindow: null,
    currentScroll: 0,
    blockCssClass: '-normacs-client-doc-block--',
    currentFragmentId: null,
    viewportChangeTimer: null,
    documentLoadingError: null
  }),

  computed: {
    ...mapGetters({
      isLoading: 'documentIsLoading',
      textContent: 'currentDocumentTextContent', // Оглавление для текстового вида
      textHtml: 'currentDocumentTextHtml', // Заглушка HTML с пустыми блоками
      textBlocks: 'currentDocumentTextBlocks', // Содержание HTML страницы
      markedText: 'markedTextBlock', // Содержание страницы с размеченными результатами поиска
      currentMatch: 'currentSearchMatch'
    }),
    emptyBlock() {
      return `<div class="${this.blockCssClass}" data-index="0"></div>`
    } 
  },

  watch: {
    documentId() {
      this.loadDocument();
    },
    fragmentId(newFragmentId) {
      if (this.currentFragmentId !== newFragmentId) {
        this.currentFragmentId = newFragmentId;
        if (newFragmentId) {
          this.scrollToAnchor(newFragmentId);
        } else {
          this.frameWindow.scrollTo(0, 0);
        }
      }
    },
    markedText(newText) {
      let el = this.frameWindow.document.querySelector('body');
      if( newText ) {
        el.innerHTML = newText;
      } else {
        el.innerHTML = this.textBlocks[0];
      }
    },
    currentMatch(newNum){
      if( newNum ) this.scrollToAnchor(`found_match_${newNum}`, 60);
    },
    $route(to) {
      if(to.hash) {
        this.currentFragmentId = to.hash.slice(1);
        this.scrollToAnchor(this.currentFragmentId);
      }
    },
    scrollTop(newScroll) {
      if(this.viewMode === 'views') {
        let el = this.frameWindow.document.querySelector('body')

        el.scrollTo(0, newScroll)
      }
    }
  },

  created() {
    this.currentFragmentId = this.fragmentId;
  },

  async mounted() {
    const frame = await this.waitFrameDocumentLoad();

    this.frameWindow = frame.contentWindow;
    try {
      await this.loadDocument();
      if(this.viewMode === 'views') {
        this.setScrollParams()
        window.addEventListener('message', this.handleFrameBodyScroll);
      }
      if (this.currentFragmentId) {
        this.scrollToAnchor(this.currentFragmentId);
      }
    } catch (err) {
      console.error('Error on load text view with: ', err);
    }
  },

  beforeDestroy() {
    if(this.viewMode === 'views') {
      this.frameWindow.removeEventListener('scroll', this.handleFrameInnerScroll)
    }  
  },

  methods: {
    waitFrameDocumentLoad() {
      return new Promise(resolve => {
        let frame = this.$refs.frame;

        if (frame.contentWindow.document.readyState === 'complete') {
          resolve(frame);
        } else {
          // For Firefox.
          let loadListener = () => {
            frame.removeEventListener('load', loadListener);
            resolve(frame);
          };
          frame.addEventListener('load', loadListener);
        }
      });
    },
    setScrollParams(){
      let idPrefix = 'NORMACS_PAGE_'
      let i = 1
      let pagesScroll = []

      while(true) {
        let markEl = this.frameWindow.document.getElementById(`${idPrefix}${i}`)

        if( !markEl ) break
        pagesScroll.push({
          id: i,
          top: markEl.getBoundingClientRect().y
        })
        i++
      }
      pagesScroll.forEach((item, ind) => {
        if( ind < (pagesScroll.length - 1)) {
          item.height = pagesScroll[ind+1].top - item.top
        } else {
          item.height = this.frameWindow.document.body.offsetHeight - item.top
        }
      })
      this.$emit('set-scroll-params', pagesScroll)
    },
    handleFrameInnerScroll(ev) {
      let el = ev.target.querySelector('body')
      let scroll = el.scrollTop

      this.$emit('html-scroll-update', scroll)
    },
    async loadDocument() {
      try {
        const textHtmlPromise = this.$store.dispatch(
          'setCurrentDocumentProperty',
          { name: 'textHtml' }
        );
        const textBlockPromise = this.$store.dispatch(
          'setCurrentDocumentProperty',
          {
            name: 'textBlock',
            param: 0
          }
        );

        await textHtmlPromise;
        await textBlockPromise;
        this.frameWindow.document.write(this.textHtml);
        this.frameWindow.document.close();

        // Condition for 2-window interface
        // if(this.viewMode === 'views') {
        //   this.frameWindow.document.body.style.overflow = 'hidden'
        // }

        let firstBlock = this.frameWindow.document.querySelector(`.${this.blockCssClass}`);

        // Для .info ссылок добавляется блок пустышка, который не приходит в head запросе
        if( !firstBlock ) {
          let emptyBody = document.createElement('body');

          emptyBody.innerHTML = `<div class="${this.blockCssClass}" data-index="0"></div>`
          let frameHtml = this.frameWindow.document.querySelector(`html`);

          frameHtml.append(emptyBody);
          firstBlock = this.frameWindow.document.querySelector(`.${this.blockCssClass}`);
        }
        
        this.insertBlockText(firstBlock, 0);
        this.frameWindow.document.addEventListener('scroll', this.handleFrameInnerScroll)
        if (!this.card.citationAllowed) {
          this.frameWindow.document.onselectstart = new Function ("return false");
          this.frameWindow.document.onmousedown = this.disableselect;
        }
        this.documentLoadingError = null;
      } catch (err) {
        const message = apiDocuments.isErrorForbidden(err)
          ? 'Доступ к документу запрещен.'
          : `Ошибка: ${err.message}`;

        this.frameWindow.document.write(message);
        this.frameWindow.document.close();
        this.documentLoadingError = err;
        throw err;
      } finally {
        if (!this.documentLoadingError) {
          this.initNormaCsLinkHandler(
            this.frameWindow.document,
            () => this.documentId
          );
        }
      }
    },
    disableselect(e) {return false},
    async getAnchorElem(anchorId) {      
      const anchorElem =
        this.frameWindow.document.getElementById(anchorId) ||
        this.frameWindow.document.querySelector(
          `a[name="${anchorId.replace('"', '\\"')}"]`
        );

      if (anchorElem) return anchorElem;
      throw new Error('Anchor element not found.');
    },
    async scrollToAnchor(anchorId, upShift) {
      try {
        let elem = await this.getAnchorElem(anchorId);
        let absOffsetTop = elem.offsetTop;

        while ((elem = elem.offsetParent)) {
          absOffsetTop += elem.offsetTop;
        }
        if (typeof absOffsetTop === 'number') {
          let shiftY = absOffsetTop - (upShift? upShift: 0);
          this.frameWindow.scrollTo(0, shiftY);
        }
      } catch (err) {
        console.warn(err);
      }
    },
    insertBlockText(block, blockIndex) {
      if (block.parentNode) {
        block.insertAdjacentHTML('afterend', this.textBlocks[blockIndex]);
        block.parentNode.removeChild(block);
      }
    }
  }
};
</script>
